// FeatureCardsSection.js — Tarjetas de características
//
// Cada tarjeta admite imagen, título, descripción, una lista de viñetas y un
// botón (que puede abrir el modal de acceso o ir a una URL). Todo el estilo de
// la tarjeta es configurable; los valores por defecto son los de siempre, así
// que los proyectos que ya la usan no cambian.
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { BACKEND_BASE, mentor2Service } from '../../../services/api';
import { buildBg } from '../bgHelpers';
import { useAuth } from '../../../contexts/AuthContext';

const Wrapper = styled.section`
  box-sizing: border-box;
  padding: ${p => p.$padding || '5rem 2rem'};
  background: ${p => p.$bg || '#fff'};
  position: relative;
  /* Remate de color en el borde superior (una franja o un degradado). */
  ${p => p.$linea && `
    &::before {
      content: '';
      position: absolute;
      top: 0; left: 0; right: 0;
      height: ${p.$lineaAlto || '4px'};
      background: ${p.$linea};
    }
  `}
  /* Foto de fondo opcional (con velo encima para que las tarjetas se lean).
     Sin imagen de fondo la sección queda exactamente igual que siempre. */
  ${p => p.$bgImage && `
    background-image: ${p.$overlay ? `linear-gradient(${p.$overlay}, ${p.$overlay}), ` : ''}url(${p.$bgImage});
    background-size: ${p.$bgSize || 'cover'};
    background-position: ${p.$bgPos || 'center'};
    background-repeat: no-repeat;
  `}
`;

const Container = styled.div`
  max-width: ${p => p.$maxWidth || '1200px'};
  margin: 0 auto;
  text-align: center;
`;

const Title = styled.h2`
  font-size: ${p => p.$size || '42px'};
  color: ${p => p.$color || '#0f355b'};
  font-weight: bold;
  font-family: ${p => p.$font || "'Myriad Pro', sans-serif"};
  margin-bottom: ${p => p.$mb || '3rem'};
  @media (max-width: 991px) { font-size: ${p => p.$sizeMobile || '32px'}; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$cols
    ? `repeat(${p.$cols}, 1fr)`
    : 'repeat(auto-fit, minmax(300px, 1fr))'};
  gap: ${p => p.$gap || '2.5rem'};
  /* Separación propia entre filas: con la foto que sobresale por arriba y el
     botón que asoma por abajo, las filas necesitan más aire que las columnas. */
  ${p => p.$gapFilas ? `row-gap: ${p.$gapFilas};` : ''}
  ${p => p.$cols ? '@media (max-width: 768px) { grid-template-columns: 1fr; }' : ''}
`;

const Card = styled.div`
  box-sizing: border-box;
  background: ${p => p.$bg || '#fff'};
  padding: ${p => p.$padding || '18px 27px'};
  border-radius: ${p => p.$radius || '111px 4px 31px 31px'};
  border: ${p => p.$borderWidth || '1px'} solid ${p => p.$borderColor || '#0f355b'};
  box-shadow: ${p => p.$shadow || 'none'};
  ${p => p.$align ? `text-align: ${p.$align};` : ''}
  ${p => p.$flex ? 'display: flex; flex-direction: column;' : ''}
  transition: 0.3s;
  ${p => p.$sinHover ? '' : `
    &:hover { transform: translateY(-5px); box-shadow: ${p.$shadowHover || '0 12px 30px rgba(43,67,97,0.1)'}; }
  `}
`;

const CardImg = styled.img`
  width: ${p => p.$w || '140px'};
  max-width: 100%;
  display: block;
  margin: ${p => p.$margin || '0 auto -20px'};
  /* Con alto + radio la imagen se comporta como un retrato recortado (foto
     circular sobre la tarjeta). Sin esas claves queda como siempre. */
  ${p => p.$h ? `height: ${p.$h}; object-fit: cover;` : ''}
  ${p => p.$radius ? `border-radius: ${p.$radius};` : ''}
  ${p => p.$border ? `border: ${p.$border};` : ''}
  ${p => p.$shadow ? `box-shadow: ${p.$shadow};` : ''}
`;

/* Segunda línea de la tarjeta (por ejemplo el docente a cargo del curso). */
const CardSubtitle = styled.div`
  color: ${p => p.$color || '#0f355b'};
  font-size: ${p => p.$size || '1rem'};
  font-weight: ${p => p.$weight || '700'};
  font-family: ${p => p.$font || 'inherit'};
  margin: ${p => p.$margin || '0 0 0.6rem'};
  line-height: 1.3;
`;

/* El título por defecto es la barra de color de toda la vida. Con
   `titulo_plano` se convierte en un encabezado normal sobre el fondo de la
   tarjeta, que es lo que pide un diseño más sobrio. */
const CardTitle = styled.h3`
  font-size: ${p => p.$size || '22px'};
  color: ${p => p.$color || '#fff'};
  background: ${p => p.$bg || 'transparent'};
  font-weight: bold;
  font-family: ${p => p.$font || "'Myriad Pro', sans-serif"};
  width: 100%;
  padding: ${p => p.$padding || '8px 0'};
  border-radius: ${p => p.$radius || '10px'};
  /* Un solo valor = separación inferior (el h3 conserva su margen propio de
     arriba). Un margen completo (con espacios) reemplaza los dos. */
  ${p => (p.$mb && /\s/.test(p.$mb.trim()))
    ? `margin: ${p.$mb};`
    : `margin-bottom: ${p.$mb || '0'};`}
`;

const CardDesc = styled.p`
  color: ${p => p.$color || '#0f355b'};
  line-height: ${p => p.$lineHeight || '1.4'};
  font-size: ${p => p.$size || '17px'};
  font-family: ${p => p.$font || "'Myriad Pro', sans-serif"};
  /* Sin la clave queda el margen propio del párrafo, como hasta ahora. */
  ${p => p.$margin ? `margin: ${p.$margin};` : ''}
`;

const Lista = styled.ul`
  list-style: ${p => p.$estilo || 'disc'};
  margin: ${p => p.$margin || '0.6rem 0 0'};
  padding-left: ${p => (p.$estilo === 'none' ? '0' : '1.2rem')};
  text-align: left;
  color: ${p => p.$color || '#0f355b'};
  font-size: ${p => p.$size || '18px'};
  font-weight: ${p => p.$weight || '700'};
  line-height: ${p => p.$lineHeight || '1.45'};
  li { margin-bottom: ${p => p.$gap || '0.35rem'}; }
  li::marker { color: ${p => p.$colorVinieta || 'inherit'}; }
`;

const Boton = styled.button`
  text-transform: ${p => p.$transform || 'none'};
  align-self: ${p => p.$align === 'left' ? 'flex-start' : p.$align === 'right' ? 'flex-end' : 'center'};
  /* Por defecto el botón se pega al pie de la tarjeta (así queda alineado entre
     columnas de distinta altura). Con una separación fija sigue al contenido. */
  /* Un solo valor = separación superior (auto lo pega al pie de la tarjeta).
     Un margen completo (con espacios) se usa tal cual: sirve para que el botón
     sobresalga del borde inferior, como en el diseño de las fichas de curso. */
  ${p => (p.$marginTop && /\s/.test(p.$marginTop.trim()))
    ? `margin: ${p.$marginTop};`
    : `margin-top: ${p.$marginTop || 'auto'};`}
  padding: ${p => p.$padding || '0.6rem 2rem'};
  background: ${p => p.$bg || '#0f355b'};
  color: ${p => p.$color || '#fff'};
  border: ${p => p.$border || 'none'};
  border-radius: ${p => p.$radius || '999px'};
  font-size: ${p => p.$size || '1rem'};
  font-weight: 700;
  font-family: inherit;
  /* El texto puede venir en dos renglones (un salto de línea en el admin);
     se centra para que los dos queden alineados aunque la tarjeta sea de
     texto a la izquierda. */
  white-space: pre-line;
  text-align: center;
  ${p => p.$lineHeight ? `line-height: ${p.$lineHeight};` : ''}
  cursor: ${p => p.$sinAccion ? 'default' : 'pointer'};
  text-decoration: none;
  display: inline-block;
  transition: 0.2s;
  &:hover:not(:disabled) {
    ${p => p.$sinAccion ? '' : 'filter: brightness(1.1); transform: translateY(-1px);'}
  }
  /* Examen todavía bloqueado: se ve, pero apagado y sin acción. */
  &:disabled { opacity: 0.45; cursor: not-allowed; transform: none; filter: none; }
`;

/* Barra de avance del curso dentro de la tarjeta. Solo se pinta cuando la
   tarjeta declara `documento_id` y hay sesión iniciada. */
const Barra = styled.div`
  width: 100%;
  height: ${p => p.$alto || '5px'};
  border-radius: 999px;
  background: ${p => p.$bg || '#cecece'};
  overflow: hidden;
  margin-top: ${p => p.$mt || '0.6rem'};
`;

const BarraFill = styled.div`
  height: 100%;
  width: ${p => p.$pct || 0}%;
  background: ${p => p.$color || '#00cfff'};
  transition: width 0.3s;
`;

const Pct = styled.div`
  margin: ${p => p.$margin || '0.4rem 0 0'};
  text-align: ${p => p.$align || 'center'};
  font-size: ${p => p.$size || '0.72rem'};
  font-weight: 700;
  color: ${p => p.$color || '#012965'};
`;

/* Fila para las tarjetas con dos botones (por ejemplo "Ir al curso" + "Examen"):
   el margen que llevaría el botón pasa a la fila. */
const BotonFila = styled.div`
  display: flex;
  gap: ${p => p.$gap || '0.6rem'};
  flex-wrap: wrap;
  justify-content: ${p => p.$align === 'left' ? 'flex-start' : p.$align === 'right' ? 'flex-end' : 'center'};
  ${p => (p.$margin && /\s/.test(p.$margin.trim()))
    ? `margin: ${p.$margin};`
    : `margin-top: ${p.$margin || 'auto'};`}
`;

const resolveImg = (v) => (!v ? null : (v.startsWith('http') ? v : `${BACKEND_BASE}/${v.replace(/^\//, '')}`));

const FeatureCardsSection = ({ config, proyecto, tl, openLogin, openRegister }) => {
  // `|| {}` para que la sección también se pueda pintar fuera del proveedor
  // de sesión (vistas previas del admin, pruebas de humo).
  const { user } = useAuth() || {};
  const c = config;
  const cards = c.cards || [];
  // Avance del usuario en los cursos que las tarjetas referencian por
  // `documento_id`. Sin esa clave no se pide nada (nada cambia en el resto).
  const [progreso, setProgreso] = useState({});
  // Detalle {total, hechas} de sesiones por curso: dice si el alumno ya vio todo
  // el curso, que es lo que habilita el examen (el porcentaje no sirve cuando el
  // examen entra en el cálculo, porque entonces nunca llega a 100 antes).
  const [leccionesDoc, setLeccionesDoc] = useState({});
  const docIds = (cards || []).map(x => x?.documento_id).filter(Boolean).map(String);
  const docIdsKey = docIds.join(',');
  // Con `progreso_incluye_examen`, en los cursos que tienen evaluación el examen
  // cuenta como un paso más: el 100% exige aprobarlo.
  const conExamen = c.progreso_incluye_examen === true;
  // El botón del examen solo se habilita cuando ya vio todas las sesiones.
  const evalRequiereLecciones = c.eval_requiere_lecciones === true;
  useEffect(() => {
    if (!user || !docIdsKey) { setProgreso({}); setLeccionesDoc({}); return; }
    let vivo = true;
    mentor2Service.progresoProyecto(docIdsKey.split(','), { incluirExamen: conExamen })
      .then(res => {
        if (!vivo || !res.data?.success) return;
        setProgreso(res.data.progreso || {});
        setLeccionesDoc(res.data.lecciones || {});
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, [user, docIdsKey, conExamen]);
  // Sin dato todavía (petición en vuelo) no se da por cumplido.
  const cursoVisto = (docId) => {
    const det = leccionesDoc[String(docId)];
    return !!det && Number(det.total) > 0 && Number(det.hechas) >= Number(det.total);
  };
  const txtEvalBloqueada = tl(c.texto_eval_bloqueada) || 'Disponible al ver todas las sesiones del curso';
  if (cards.length === 0) return null;
  const secondary = c.color_secundario || proyecto?.color_secundario || '#14b6cb';
  const primary = c.color_primario || proyecto?.color_primario || '#0f355b';
  const tituloPlano = c.titulo_plano === true;

  // Acción del botón de una tarjeta: modal de acceso, ruta interna o URL.
  // `boton_destino_logueado` permite el patrón "si no hay sesión, entrar; si ya
  // entró, ir al curso": sin esa clave el botón se comporta como siempre.
  // `s` = sufijo de la clave: '' es el botón de siempre y '2' el segundo botón
  // opcional de la tarjeta (el de la evaluación, por ejemplo).
  const renderBoton = (card, i, s = '', enFila = false) => {
    const texto = tl(card[`boton${s}_texto`]);
    if (!texto) return null;
    const destino = ((user && card[`boton${s}_destino_logueado`]) || card[`boton${s}_destino`] || '').trim();
    // Botón de examen (destino /evaluacion/...) todavía bloqueado: se pinta
    // apagado y no navega hasta que el alumno vea todas las sesiones.
    const bloqueado = evalRequiereLecciones && !!user
      && /^\/evaluacion\//.test(destino) && !!card.documento_id && !cursoVisto(card.documento_id);
    const props = {
      // La tarjeta puede pisar el color del botón: sirve para marcar una ficha
      // distinta (por ejemplo "próximo lanzamiento") sin duplicar la sección.
      $bg: card[`boton${s}_color`] || c[`color_boton${s}`] || c.color_boton || primary,
      $color: card[`boton${s}_color_texto`] || c[`color_texto_boton${s}`] || c.color_texto_boton || '#fff',
      $border: card[`boton${s}_borde`] || c[`borde_boton${s}`] || c.borde_boton,
      $lineHeight: c.interlineado_boton || (/\n/.test(texto) ? '1' : null),
      $radius: c.radio_boton,
      $padding: c.padding_boton,
      $size: c.tamano_boton,
      $align: c.alineacion_boton || 'center',
      $transform: c.transform_boton,
      // Dentro de la fila el margen lo pone la fila.
      $marginTop: enFila ? '0' : c.margen_boton,
    };
    if (bloqueado) {
      return (
        <Boton key={`b${s}${i}`} type="button" disabled title={txtEvalBloqueada} {...props}>
          🔒 {texto}
        </Boton>
      );
    }
    if (destino === 'ninguno') {
      return <Boton key={`b${s}${i}`} as="span" role="note" $sinAccion {...props}>{texto}</Boton>;
    }
    if (destino === 'login' || destino === '') {
      return <Boton key={`b${s}${i}`} type="button" {...props} onClick={() => openLogin?.()}>{texto}</Boton>;
    }
    if (destino === 'registro') {
      return <Boton key={`b${s}${i}`} type="button" {...props} onClick={() => openRegister?.()}>{texto}</Boton>;
    }
    if (/^https?:\/\//i.test(destino)) {
      return <Boton key={`b${s}${i}`} as="a" href={destino} target="_blank" rel="noopener noreferrer" {...props}>{texto}</Boton>;
    }
    return <Boton key={`b${s}${i}`} as={Link} to={destino} {...props}>{texto}</Boton>;
  };

  return (
    <Wrapper
      $bg={buildBg(c, '#fff')}
      $padding={c.padding}
      $bgImage={c.imagen_fondo ? resolveImg(c.imagen_fondo) : null}
      $overlay={c.overlay_fondo}
      $bgSize={c.tamano_fondo}
      $bgPos={c.posicion_fondo}
      $linea={c.linea_superior}
      $lineaAlto={c.linea_superior_alto}
    >
      <Container $maxWidth={c.ancho_maximo}>
        {c.titulo && (
          <Title
            $color={c.color_titulo || primary}
            $size={c.tamano_titulo}
            $sizeMobile={c.tamano_titulo_movil}
            $font={c.fuente_titulo}
            $mb={c.margen_titulo}
          >
            {tl(c.titulo)}
          </Title>
        )}
        <Grid $cols={parseInt(c.columnas) || null} $gap={c.gap} $gapFilas={c.gap_filas}>
          {cards.map((card, i) => (
            <Card
              key={i}
              $bg={c.color_tarjeta}
              $borderColor={c.color_borde || primary}
              $borderWidth={c.grosor_borde}
              $radius={c.radio_tarjeta}
              $padding={c.padding_tarjeta}
              $shadow={c.sombra_tarjeta}
              $shadowHover={c.sombra_tarjeta_hover}
              $align={c.alineacion_texto}
              $flex={!!(card.boton_texto || card.boton2_texto)}
              $sinHover={c.hover_efecto === false}
            >
              {card.imagen && (
                <CardImg
                  src={resolveImg(card.imagen)}
                  alt={tl(card.titulo)}
                  $w={c.ancho_imagen}
                  $margin={c.margen_imagen}
                  $h={c.alto_imagen}
                  $radius={c.radio_imagen}
                  $border={c.borde_imagen}
                  $shadow={c.sombra_imagen}
                />
              )}
              {card.titulo && (
                <CardTitle
                  $bg={tituloPlano ? 'transparent' : (c.color_fondo_titulo_tarjeta || secondary)}
                  $color={tituloPlano ? (c.color_titulo_tarjeta || primary) : (c.color_titulo_tarjeta || '#fff')}
                  $size={c.tamano_titulo_tarjeta}
                  $padding={tituloPlano ? (c.padding_titulo_tarjeta || '0') : c.padding_titulo_tarjeta}
                  $radius={tituloPlano ? '0' : c.radio_titulo_tarjeta}
                  $font={c.fuente_titulo_tarjeta}
                  $mb={c.margen_titulo_tarjeta}
                >
                  {tl(card.titulo)}
                </CardTitle>
              )}
              {card.subtitulo && (
                <CardSubtitle
                  $color={c.color_subtitulo_tarjeta || primary}
                  $size={c.tamano_subtitulo_tarjeta}
                  $weight={c.peso_subtitulo_tarjeta}
                  $font={c.fuente_subtitulo_tarjeta}
                  $margin={c.margen_subtitulo_tarjeta}
                >
                  {tl(card.subtitulo)}
                </CardSubtitle>
              )}
              {card.descripcion && (
                <CardDesc
                  $color={c.color_descripcion || primary}
                  $size={c.tamano_descripcion}
                  $font={c.fuente_descripcion}
                  $margin={c.margen_descripcion}
                  $lineHeight={c.interlineado_descripcion}
                >
                  {tl(card.descripcion)}
                </CardDesc>
              )}
              {Array.isArray(card.items) && card.items.length > 0 && (
                <Lista
                  $estilo={c.estilo_lista}
                  $color={c.color_lista || primary}
                  $colorVinieta={c.color_vineta}
                  $size={c.tamano_lista}
                  $weight={c.peso_lista}
                  $lineHeight={c.interlineado_lista}
                  $gap={c.gap_lista}
                  $margin={c.margen_lista}
                >
                  {card.items.map((it, j) => (
                    <li key={j}>{tl(typeof it === 'string' ? it : (it?.texto ?? it))}</li>
                  ))}
                </Lista>
              )}
              {card.documento_id && user ? (
                <div style={{ width: '100%' }}>
                  <Barra $bg={c.color_barra} $alto={c.alto_barra} $mt={c.margen_barra}>
                    <BarraFill
                      $pct={Number(progreso[String(card.documento_id)] || 0)}
                      $color={c.color_barra_avance}
                    />
                  </Barra>
                  <Pct
                    $color={c.color_pct}
                    $size={c.tamano_pct}
                    $align={c.alineacion_pct || c.alineacion_texto || 'center'}
                    $margin={c.margen_pct}
                  >
                    {Number(progreso[String(card.documento_id)] || 0)}% {tl(c.texto_progreso) || 'Completado'}
                  </Pct>
                </div>
              ) : null}
              {card.boton2_texto
                ? (
                  <BotonFila $align={c.alineacion_boton} $margin={c.margen_boton} $gap={c.gap_botones}>
                    {renderBoton(card, i, '', true)}
                    {renderBoton(card, i, '2', true)}
                  </BotonFila>
                )
                : renderBoton(card, i)}
            </Card>
          ))}
        </Grid>
      </Container>
    </Wrapper>
  );
};

export default FeatureCardsSection;
